import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "@assets/icon";
import { chatService } from "../../../../services/chatService";
import "./DislikeBlock.scss";

interface DislikeBlockProps {
  onCloseDislikeBlock: () => void;
  /** reasonIds: id các chip đã chọn (rỗng nếu đang dùng danh sách fallback không có id) */
  onSubmitReason: (reason: string, reasonIds: string[]) => void;
  onShowToast: () => void;
}

interface DislikeReasonOption {
  id?: string;
  text: string;
}

const DislikeBlock: React.FC<DislikeBlockProps> = ({
  onCloseDislikeBlock,
  onSubmitReason,
  onShowToast
}) => {
  // Cho chọn nhiều lý do, mặc định không chọn gì
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [customReason, setCustomReason] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  
  const dislikePopupRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const { t } = useTranslation();

  // Danh sách chip động từ GET /dislike-reasons; fallback i18n (không có id) khi API lỗi / rỗng
  const [apiReasons, setApiReasons] = useState<DislikeReasonOption[] | null>(null);
  useEffect(() => {
    const token = sessionStorage.getItem('access_token');
    if (!token) return;
    let cancelled = false;
    chatService
      .getDislikeReasons(token)
      .then((reasons) => {
        if (!cancelled && reasons.length > 0) setApiReasons(reasons);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const dislikeReasons: DislikeReasonOption[] = apiReasons ?? [
    { text: t("common.dislikeReasonNotAccurate") },
    { text: t("common.dislikeReasonIncomplete") },
    { text: t("common.dislikeReasonNotMatch") },
    { text: t("common.dislikeReasonTooLong") },
    { text: t("common.dislikeReasonOther") },
  ];

  // Chip "Khác" (cả từ API lẫn fallback) → mở textarea nhập lý do
  const isOtherReason = (text: string) =>
    text.trim().toLowerCase() === t("common.dislikeReasonOther").trim().toLowerCase();
  // selectedReasons lưu theo text → giữ nguyên lựa chọn khi list API về thay list fallback
  const isOtherSelected = selectedReasons.some(isOtherReason);
  const canSubmit =
    selectedReasons.length > 0 && (!isOtherSelected || Boolean(customReason.trim()));

  const handleClickDislikeReason = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  const handleDislikeReasonSubmit = () => {
    if (!canSubmit) return;

    const reasonIds = dislikeReasons
      .filter((reason) => reason.id && selectedReasons.includes(reason.text))
      .map((reason) => reason.id as string);
    // dislikeReason (text, max 256): nối các lý do đã chọn; "Khác" thay bằng nội dung user nhập
    const finalReason = selectedReasons
      .map((reason) => (isOtherReason(reason) ? customReason.trim() : reason))
      .join("; ")
      .slice(0, 256);

    onSubmitReason(finalReason, reasonIds);
    onShowToast();
    onCloseDislikeBlock();

    setSelectedReasons([]);
    setCustomReason("");
  };

  const handleClose = () => {
    setSelectedReasons([]);
    setCustomReason("");
    setIsUserTyping(false);
    onShowToast();
    onCloseDislikeBlock();
  };
  
  useEffect(() => {
    if (!isUserTyping) {
      const timer = setTimeout(() => {
        onShowToast();
        onCloseDislikeBlock();
      }, 10000);
      
      return () => clearTimeout(timer);
    }
    // selectedReasons: mỗi lần chọn/bỏ chọn lý do thì đếm lại 10s
  }, [isUserTyping, selectedReasons, onCloseDislikeBlock, onShowToast]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dislikePopupRef.current && !dislikePopupRef.current.contains(event.target as Node)) {
        handleClose();
        onShowToast();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  useEffect(() => {
    if (dislikePopupRef.current) {
      setTimeout(() => {
        dislikePopupRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }, 100);
    }
  }, []);
  
  return (
    <div className="chat-admin flex flex-col mt-[10px]">
      <div className="flex items-center">
        <div className="flex flex-col flex-1 pl-3">
          <div 
            ref={dislikePopupRef} 
            className="dislike-block"
          >
            <div className="dislike-block-header-wrapper">
              <div className="dislike-block-header">
                {t("common.dislikePopupTitle")}
              </div>
              <button
                className="dislike-block-close"
                onClick={handleClose}
                aria-label="Đóng"
              >
                <CloseIcon />
              </button>
            </div>
            
            <div className="dislike-block-buttons">
              <div className="dislike-block-buttons__row">
                {dislikeReasons.slice(0, 2).map(({ text: reason }) => (
                  <button
                    key={reason}
                    type="button"
                    aria-pressed={selectedReasons.includes(reason)}
                    className={`dislike-reason-btn ${selectedReasons.includes(reason) ? 'selected' : ''}`}
                    onClick={() => handleClickDislikeReason(reason)}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <div className="dislike-block-buttons__row">
                {dislikeReasons.slice(2).map(({ text: reason }) => (
                  <button
                    key={reason}
                    type="button"
                    aria-pressed={selectedReasons.includes(reason)}
                    className={`dislike-reason-btn ${selectedReasons.includes(reason) ? 'selected' : ''}`}
                    onClick={() => handleClickDislikeReason(reason)}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>
            
            {selectedReasons.length > 0 && (
              <div className="dislike-custom-input">
                {isOtherSelected && (
                  <textarea
                    ref={textareaRef}
                    placeholder={t("common.dislikeCustomPlaceholder")}
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    onFocus={() => setIsUserTyping(true)}
                    onBlur={() => setIsUserTyping(false)}
                    className="custom-reason-textarea"
                    rows={3}
                    maxLength={256}
                  />
                )}
                <div className="send-reason-wrapper">
                  <button
                    type="button"
                    className={`send-reason-btn ${canSubmit ? 'is-active' : ''}`}
                    onClick={handleDislikeReasonSubmit}
                    disabled={!canSubmit}
                  >
                    {t("common.send")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DislikeBlock;